import { AutocompleteChangeReason, TextField } from "@mui/material";
import { IconCollection } from "components/Icon/IconCollection";
import { Callout } from "components/Form/components/Callout/Callout";
import IconButton from "components/IconButton";
import { Popover, popoverPosition } from "components/Popover/Popover";
import { RoleSelectAutocomplete } from "components/RoleSelect/RoleSelectAutocomplete";
import Tooltip from "components/Tooltip/Tooltip";
import {
    create as createIdentityRole,
    remove as deleteIdentityRole,
    update as updateIdentityRole,
} from "containers/Auth/screens/IdentityManagement/IdentityRole.service";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./RoleSelect.scss";

export type RoleSelectProps = {
    identity: any;
    /** Show the per-role "required" flag (roles of a flow, wrapper or scheduler). */
    showRequired?: boolean;
    onSelect: () => void;
    onDeselect: () => void;
    onUpdate: () => void;
};

export const rolesAutocompleteSelector = (state: any) => state?.dataStore?.rolesAutocomplete ?? [];

const RoleSelect = ({
    identity,
    showRequired = false,
    onSelect = () => {},
    onDeselect = () => {},
    onUpdate = () => {},
}: RoleSelectProps) => {
    const dispatch = useDispatch();

    const identityId = identity.id;
    const roles = useSelector(rolesAutocompleteSelector);
    const currentlySelectedRoleRelations = identity.identity_role_plain_list_identity_id;
    const [isLoading, setIsLoading] = useState(false);

    const onSelectHandler = (roleId: string) => {
        setIsLoading(true);
        createIdentityRole(dispatch, identityId, roleId)
            .then(() => onSelect())
            .finally(() => setIsLoading(false));
    };

    const onDeselectHandler = (identityRoleId: string, e: any) => {
        e.preventDefault();

        setIsLoading(true);
        deleteIdentityRole(dispatch, identityRoleId)
            .then(() => onDeselect())
            .finally(() => setIsLoading(false));
    };

    const onChangePropagateHandler = (identityRoleId: string, propagate: boolean) => {
        setIsLoading(true);
        updateIdentityRole(dispatch, identityRoleId, propagate)
            .then(() => onUpdate())
            .finally(() => setIsLoading(false));
    };

    const onChangeRequiredHandler = (identityRoleId: string, required: boolean) => {
        setIsLoading(true);
        updateIdentityRole(dispatch, identityRoleId, undefined, required)
            .then(() => onUpdate())
            .finally(() => setIsLoading(false));
    };

    const notRequiredRoleNames: string[] = showRequired
        ? (currentlySelectedRoleRelations ?? [])
              .filter((relation: any) => relation?.required === false)
              .map((relation: any) => relation?.role_id_role?.name)
              .filter(Boolean)
        : [];

    const isDisabled = identity.is_readonly || !!identity.deleted_at;

    return (
        <Tooltip overlay="Roles">
            <Popover
                trigger={<IconButton icon={IconCollection.shieldAccountOutline} />}
                contentClassName="roleSelectContent"
                closeOnInsideClick={false}
                {...popoverPosition}
            >
                <RoleSelectAutocomplete
                    options={roles}
                    selectedValue={currentlySelectedRoleRelations}
                    renderInput={(params) => (
                        <TextField {...params} label="Roles" placeholder="Add role" />
                    )}
                    onDelete={(e, val) => onDeselectHandler(val.id, e)}
                    onPropagateChange={(event, val) => {
                        onChangePropagateHandler(val.id, event.target.checked);
                    }}
                    onAddRole={(
                        event: React.SyntheticEvent,
                        value: any[],
                        reason: AutocompleteChangeReason
                    ) => {
                        if (reason === "selectOption") {
                            const addedRole = value.find((v) => !v.role_id_role);
                            if (addedRole) onSelectHandler(addedRole.id);
                        }
                    }}
                    onRequiredChange={
                        showRequired
                            ? (event, val) => onChangeRequiredHandler(val.id, event.target.checked)
                            : undefined
                    }
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                />
                {showRequired && (
                    <p className="roleSelectHint">
                        Executions of this record run with these roles. A required role must be
                        held by whoever starts it.
                    </p>
                )}
                {notRequiredRoleNames.length > 0 && (
                    <Callout
                        type="warning"
                        className="roleSelectNotRequiredWarning"
                        text={`Not required: ${notRequiredRoleNames.join(", ")}. Everyone who can start this record gets ${notRequiredRoleNames.length === 1 ? "this role" : "these roles"}, even without holding ${notRequiredRoleNames.length === 1 ? "it" : "them"}.`}
                    />
                )}
            </Popover>
        </Tooltip>
    );
};

export default RoleSelect;
